import { eachDayOfInterval, format } from 'date-fns';

import { api } from '../ducks/usage-details';
import { API_DATE_FORMAT } from '../constants';

export const calculateDailyBurnsPerDevice = (
  dateRange: {string: Date},
  burnDaily: DailyBurnResponse,
): Array<Array<ChartDailyBurnEntry>> => (
  eachDayOfInterval({ start: dateRange.startDate, end: dateRange.endDate })
    .map((date) => {
      const burns = burnDaily
        .find((burn) => burn.day === format(date, API_DATE_FORMAT)) ||
        { resource_burn: [] };

      return {
        day: new Date(date),
        ...(burns.resource_burn.reduce((acc, resource) => {
          acc[resource.resource_type] = resource.credits;
          return acc as ChartDailyBurnEntry;
        }, {})),
      } as Array<ChartDailyBurnEntry>;
    }));

export const calculateTotalBurnsPerDevice = (
  dailyBurns: Array<Array<ChartDailyBurnEntry>>,
): ChartTotalBurnEntry => (
  dailyBurns.reduce((acc, dailyBurn) => {
    Object.keys(dailyBurn).forEach((resourceNameOrDay) => {
      if (resourceNameOrDay !== 'day') {
        if (acc[resourceNameOrDay] === undefined) {
          acc[resourceNameOrDay] = dailyBurn[resourceNameOrDay];
        } else {
          acc[resourceNameOrDay] += dailyBurn[resourceNameOrDay];
        }
      }
    });
    return acc;
  }, { }) as { [key: string]: number });

export const calculateTotalBurn = (
  totalBurnsDict: ChartTotalBurnEntry,
): number => (
  Object.values(totalBurnsDict)
    .reduce((acc, value) => (acc as number) + (value as number), 0)) as number;

export const getOrderedDeviceKeys = (
  totalBurnsDict: ChartTotalBurnEntry,
  selectedDevices: Array<string>,
): Array<string> => {
  const sortedTotalBurnsEntries = Object.entries(totalBurnsDict)
    .sort(([, val1], [, val2]) => val2 - val1);
  return Object.values(sortedTotalBurnsEntries)
    .map((value) => value[0])
    .filter((deviceName) => selectedDevices.includes(deviceName));
};

export interface MonthlySummaries {
  average: number,
  monthsUsedForAverage: number,
  peak: number,
  peakPeriodName: string,
}

export const monthlySummaries = (
  burns: Array<MonthlyBurnEntry>,
): MonthlySummaries => {
  // First, we find which period had peak usage
  const peak = burns.reduce((acc, entry) => {
    if (entry.credits_burnt > acc.credits_burnt) {
      return entry;
    }
    return acc;
  }, { credits_burnt: -1, start_day: null, end_day: null });

  // We need a name for the peak period,
  // it's either a single month, a turn of months, or a turn of years
  const peakStartMonth = new Date(peak.start_day).toLocaleString('default', { month: 'long' });
  const peakStartYear = new Date(peak.start_day).toLocaleString('default', { year: 'numeric' });
  const peakEndMonth = new Date(peak.end_day).toLocaleString('default', { month: 'long' });
  const peakEndYear = new Date(peak.end_day).toLocaleString('default', { year: 'numeric' });

  let peakPeriodName = `${peakStartMonth}/${peakEndMonth} ${peakEndYear}`;
  if (peakStartYear !== peakEndYear) {
    peakPeriodName = `${peakStartMonth} ${peakStartYear}/${peakEndMonth} ${peakEndYear}`;
  }
  if (peakStartMonth === peakEndMonth) {
    peakPeriodName = `${peakEndMonth} ${peakEndYear}`;
  }

  // We don't want periods without any burns to calculate the average,
  // but we also need a minimum of 2 periods
  const sortedBurns = burns.sort((left, right) => left.start_day.localeCompare(right.start_day));
  const monthsUsedForAverage = sortedBurns[0].credits_burnt > 0 ? 3 : 2;

  return {
    average: Math.round(
      sortedBurns.reduce((acc, entry) => (
        acc + entry.credits_burnt
      ), 0) / monthsUsedForAverage,
    ),
    monthsUsedForAverage,
    peak: peak.credits_burnt,
    peakPeriodName,
  };
};
